/**
 * The side navigation (PM efb0e798): the icon rail, the chosen section's
 * page list beside it, and the build version at the foot.
 *
 *  desktop, expanded   rail + page list, docked under the top bar
 *  desktop, collapsed  rail alone; choosing a section opens its page list
 *                      as a flyout beside the rail
 *  phone               the whole thing is a slide-in drawer, opened from
 *                      the top bar's menu button
 *
 * Which section shows is decided by the layout (it also sets the content
 * margin); this component renders it and reports picks and dismissals.
 */
import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import NavRail from "./nav/NavRail";
import SectionNav from "./nav/SectionNav";
import type { RailSection } from "./nav/railModel";
import { getMutedText } from "../../utils/themeUtils";

export const RAIL_WIDTH_CLASS = "ml-20";
export const RAIL_AND_PANEL_WIDTH_CLASS = "ml-[304px]";

interface SideNavProps {
  sections: RailSection[];
  activeKey: string | null;
  activeHref: string | null;
  /** the section whose page list is showing, if any */
  shownSection: RailSection | null;
  /** desktop collapsed: the page list is a flyout over the content */
  collapsed: boolean;
  isMobileDevice: boolean;
  drawerOpen: boolean;
  onCloseDrawer: () => void;
  onPick: (key: string) => void;
  /** close a flyout (desktop collapsed) */
  onDismiss: () => void;
  isDarkMode: boolean;
  versionLabel?: string;
  versionTitle?: string;
  /** shown at the top of the phone drawer (call status) */
  drawerExtras?: React.ReactNode;
}

const SideNav: React.FC<SideNavProps> = ({
  sections,
  activeKey,
  activeHref,
  shownSection,
  collapsed,
  isMobileDevice,
  drawerOpen,
  onCloseDrawer,
  onPick,
  onDismiss,
  isDarkMode,
  versionLabel,
  versionTitle,
  drawerExtras,
}) => {
  const asideRef = useRef<HTMLElement>(null);
  const flyout = !isMobileDevice && collapsed && !!shownSection;

  // Escape closes the phone drawer or the collapsed flyout; so does a click
  // outside the navigation.
  useEffect(() => {
    const drawerShowing = isMobileDevice && drawerOpen;
    if (!drawerShowing && !flyout) return;
    const close = () => (drawerShowing ? onCloseDrawer() : onDismiss());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onDown = (e: MouseEvent) => {
      if (flyout && asideRef.current && !asideRef.current.contains(e.target as Node)) close();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [isMobileDevice, drawerOpen, flyout, onCloseDrawer, onDismiss]);

  const onNavigate = () => {
    if (isMobileDevice) onCloseDrawer();
  };

  const positionCls = isMobileDevice
    ? `top-0 h-full z-50 shadow-2xl transition-transform duration-300 ease-in-out ${
        drawerOpen ? "translate-x-0" : "-translate-x-full invisible"
      }`
    : "top-14 bottom-0 z-30";

  return (
    <>
      {isMobileDevice && drawerOpen && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={onCloseDrawer} aria-hidden="true" />
      )}
      <aside
        ref={asideRef}
        className={`fixed left-0 flex max-w-[85vw] ${positionCls}`}
        data-testid="nav-drawer"
        data-state={isMobileDevice ? (drawerOpen ? "open" : "closed") : "docked"}
      >
        {/*
          A <nav> landmark: the primary navigation for the whole signed-in
          app (staff sections and family links alike), so screen-reader users
          can jump to it and `page.locator('nav')` finds it.
        */}
        <nav aria-label="Main" data-testid="navigation" className="flex h-full flex-col">
          {isMobileDevice && (
            <div
              className={`flex h-14 flex-shrink-0 items-center justify-between gap-2 px-2 border-b ${
                isDarkMode ? "bg-gray-900 text-gray-100 border-gray-700" : "bg-white text-gray-800 border-gray-200"
              }`}
            >
              <div className="min-w-0">{drawerExtras}</div>
              <button
                type="button"
                onClick={onCloseDrawer}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          )}
          <div className="relative flex min-h-0 flex-1">
            <NavRail
              sections={sections}
              activeKey={activeKey}
              shownKey={shownSection?.key ?? null}
              onPick={onPick}
              onNavigate={onNavigate}
              isDarkMode={isDarkMode}
            />
            {shownSection ? (
              <SectionNav
                section={shownSection}
                activeHref={activeHref}
                isDarkMode={isDarkMode}
                onNavigate={onNavigate}
                className={flyout ? "absolute left-20 top-0 bottom-0 z-40 shadow-2xl" : ""}
              />
            ) : (
              isMobileDevice && (
                <div
                  className={`w-56 flex-shrink-0 p-4 text-sm ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  } ${getMutedText(isDarkMode)}`}
                >
                  Choose a section to see its pages.
                </div>
              )
            )}
          </div>
          <div
            className={`flex-shrink-0 border-t px-2 py-1 text-[10px] truncate select-text ${
              isDarkMode ? "bg-gray-900 text-gray-400 border-gray-700" : "bg-gray-50 text-gray-500 border-gray-200"
            }`}
            title={versionTitle}
            data-testid="nav-version"
          >
            {/* The rail alone is too narrow for the word "Version". */}
            {!isMobileDevice && (collapsed || !shownSection)
              ? versionLabel || "?"
              : `Version ${versionLabel || "unknown"}`}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default SideNav;
